import { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const NAV_MOBILE = [
  { to: '/dashboard', label: 'Accueil', icon: IconHome },
  { to: '/membres', label: 'Membres', icon: IconMembers },
  { to: '/agenda', label: 'Agenda', icon: IconCalendar },
  { to: '/statistiques', label: 'Stats', icon: IconStats },
  { to: '/profil', label: 'Profil', icon: IconUser },
];

const NAV_DESKTOP = [
  { section: 'Principal' },
  { to: '/dashboard', label: 'Tableau de bord', icon: IconHome },
  { to: '/membres', label: 'Membres', icon: IconMembers },
  { to: '/agenda', label: 'Agenda', icon: IconCalendar },
  { to: '/statistiques', label: 'Statistiques', icon: IconStats },
  { section: 'Business' },
  { to: '/merci', label: 'Merci Business', icon: IconMerci },
  { to: '/contacts', label: 'Contacts', icon: IconContacts, roles: ['animateur','franchise','super_admin'] },
  { section: 'Admin' },
  { to: '/formations', label: 'Formations', icon: IconDoc, soon: true },
  { to: '/faq', label: 'FAQ', icon: IconFaq, soon: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => { logout(); toast.success('À bientôt !'); navigate('/login'); };
  const initials = `${user?.prenom?.[0]||''}${user?.nom?.[0]||''}`.toUpperCase();
  const getRoleLabel = () => {
    if (user?.roles?.includes('super_admin')) return 'Super Administrateur';
    if (user?.roles?.includes('franchise')) return 'Franchisé';
    if (user?.roles?.includes('animateur')) return 'Animateur';
    return 'Membre';
  };
  const canSee = (roles) => !roles || roles.some(r => user?.roles?.includes(r));

  return (
    <div className="mb-layout">
      {sidebarOpen && <div onClick={() => setSidebarOpen(false)} style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:99 }} />}

      <aside className={`mb-sidebar${sidebarOpen?' open':''}`}>
        <div className="mb-sidebar-logo">
          <div className="mb-logo-icon">MB</div>
          <div>
            <div className="mb-logo-name">Matin Business</div>
            <div className="mb-logo-role">{getRoleLabel()}</div>
          </div>
        </div>
        <nav className="mb-nav">
          {NAV_DESKTOP.filter(n => !n.roles || canSee(n.roles)).map((item, i) => {
            if (item.section) return <div key={i} className="mb-nav-section">{item.section}</div>;
            const Icon = item.icon;
            if (item.soon) return (
              <div key={item.to} className="mb-nav-item" style={{opacity:0.4,cursor:'default'}}>
                <span className="mb-nav-icon"><Icon /></span>{item.label}
                <span className="mb-nav-soon">bientôt</span>
              </div>
            );
            return (
              <NavLink key={item.to} to={item.to} className={({isActive})=>`mb-nav-item${isActive?' active':''}`}>
                <span className="mb-nav-icon"><Icon /></span>{item.label}
                {item.badge && <span className="mb-nav-badge">{item.badge}</span>}
              </NavLink>
            );
          })}
        </nav>
        <div className="mb-sidebar-user">
          <div className="mb-user-avatar">
            {user?.photo_url ? <img src={user.photo_url} alt="" /> : initials}
          </div>
          <div style={{minWidth:0,flex:1}}>
            <div className="mb-user-name">{user?.prenom} {user?.nom}</div>
            <div className="mb-user-role">{user?.email}</div>
          </div>
          <button className="mb-logout-btn" onClick={handleLogout} title="Déconnexion">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          </button>
        </div>
      </aside>

      <main className="mb-main">
        <Outlet context={{ setSidebarOpen }} />
      </main>

      <nav className="mb-tab-bar">
        {NAV_MOBILE.map(item => {
          const isActive = location.pathname.startsWith(item.to);
          const Icon = item.icon;
          return (
            <NavLink key={item.to} to={item.to} className="mb-tab">
              <div className={`mb-tab-ico${isActive?' active':''}`}><Icon active={isActive} /></div>
              <span className={`mb-tab-lbl${isActive?' active':''}`}>{item.label}</span>
              {isActive && <div className="mb-tab-dot" />}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}

function IconHome({active}) { return <svg width="18" height="18" viewBox="0 0 24 24" fill={active?"#95C11F":"#8e8e93"}><rect x="3" y="3" width="8" height="8" rx="1.5"/><rect x="13" y="3" width="8" height="8" rx="1.5"/><rect x="3" y="13" width="8" height="8" rx="1.5"/><rect x="13" y="13" width="8" height="8" rx="1.5"/></svg>; }
function IconMembers({active}) { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={active?"#95C11F":"#8e8e93"} strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>; }
function IconCalendar({active}) { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={active?"#95C11F":"#8e8e93"} strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>; }
function IconStats({active}) { return <svg width="18" height="18" viewBox="0 0 24 24" fill={active?"#95C11F":"#8e8e93"}><rect x="3" y="14" width="4" height="7"/><rect x="10" y="9" width="4" height="12"/><rect x="17" y="4" width="4" height="17"/></svg>; }
function IconUser({active}) { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={active?"#95C11F":"#8e8e93"} strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M20 21a8 8 0 10-16 0"/></svg>; }
function IconContacts() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="23" y1="11" x2="17" y2="11"/><line x1="20" y1="8" x2="20" y2="14"/></svg>; }
function IconMerci() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 12V22H4V12"/><path d="M22 7H2v5h20V7z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/></svg>; }
function IconDoc() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>; }
function IconFaq() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>; }
